import { StoreState } from '..';
import {
  POLICYSTREAM_FORMS,
  POLICYSTREAM_API_NAMES,
  POLICY_TERM_YEARS,
} from '../../components/PolicyStream/constants';
import { MILLISECONDS_PER_YEAR } from '../../util/constants';
import { StructuredPolicyStreamRatingData } from '../policystreamStore';
import { findNearest } from '../../util/findNearest';
import { isNotNullish } from '../../util';
import { QuoteWizardAnsweredForm } from '../../components/QuoteWizard/classes';
import Bugsnag from '@bugsnag/browser';
import { errorify } from '@calefy-inc/utility';

const COVERAGE_MIN = 100_000;
const COVERAGE_MAX = 5_000_000;
const COVERAGE_STEP = 50_000;
const possibleCoverageAmounts = new Array(
  (COVERAGE_MAX - COVERAGE_MIN) / COVERAGE_STEP + 1,
)
  .fill(0)
  .map((_, index) => COVERAGE_MIN + index * COVERAGE_STEP);
export const getRecommendedCoverageAmount = (
  state: StoreState,
): StructuredPolicyStreamRatingData['coverageAmountDollars'] | null => {
  const recommendationForm = Object.values(
    state.policystreamStore.formAnswers,
  ).find(
    (answeredForm) =>
      answeredForm.form.internalName === POLICYSTREAM_FORMS.needsAnalysis,
  );
  if (!recommendationForm) {
    return null;
  }
  const debtAmount = getPolicyStreamDebtAmount(recommendationForm);
  const incomeReplacementAmount =
    getPolicyStreamIncomeReplacementAmout(recommendationForm);
  const mortgageAmount = getPolicyStreamMortgageAmount(recommendationForm);
  const educationAmount = getPolicyStreamEducationAmount(recommendationForm);
  // NB These will be negative or 0
  const existingLifeInsuranceAmount =
    getPolicyStreamExistingLifeInsuranceAmount(recommendationForm);
  const existingInvestmentsAmount =
    getPolicyStreamExistingInvestmentsAmount(recommendationForm);
  const coverageFactors = [
    debtAmount,
    incomeReplacementAmount,
    mortgageAmount,
    educationAmount,
    existingLifeInsuranceAmount,
    existingInvestmentsAmount,
  ];

  if (coverageFactors.every(isNotNullish)) {
    const rawCoverageAmount = coverageFactors.reduce(
      (coverageAmount, coverageFactor) => {
        return coverageAmount + coverageFactor;
      },
      0,
    );
    // as per phil, we don't want to limit this anymore
    const adjusted = rawCoverageAmount;
    // rawCoverageAmount > 0
    //   ? findNearest(rawCoverageAmount, possibleCoverageAmounts)
    //   : rawCoverageAmount;
    return adjusted;
  }

  return null;
};

/**
 * Given the raw recommendation, return something suitable for use in the selection slider.
 */
export const getPolicyStreamRecommendationSelection = (raw: number): number => {
  const formattedRecommendation =
    raw > 0 ? findNearest(raw, possibleCoverageAmounts) : raw;
  return formattedRecommendation;
};

/**
 * Get the amount of total investments. This will be negative (or 0)
 */
const getPolicyStreamExistingInvestmentsAmount = (
  form: QuoteWizardAnsweredForm,
): number => {
  const rawAmount = form.getAnswerValueByApiName(
    POLICYSTREAM_API_NAMES.needsAnalysis.existingSavingsInvestmentsAmount,
  );
  const amount = convertToNumber(rawAmount);
  return amount !== null ? -amount : 0;
};

/**
 * Get the amount of total existing life insurance. This will be negative (or 0)
 */
const getPolicyStreamExistingLifeInsuranceAmount = (
  form: QuoteWizardAnsweredForm,
): number => {
  const rawAmount = form.getAnswerValueByApiName(
    POLICYSTREAM_API_NAMES.needsAnalysis.existingLifeInsuranceAmount,
  );
  const amount = convertToNumber(rawAmount);
  return amount !== null ? -amount : 0;
};
/**
 * Get the total debt amount. This should be the outstanding debt + $15,000 for funeral, &c. expenses
 */
const getPolicyStreamDebtAmount = (
  form: QuoteWizardAnsweredForm,
): number | null => {
  const FINAL_EXPENSES = 15_000;
  const outstandingDebt = form.getAnswerValueByApiName(
    POLICYSTREAM_API_NAMES.needsAnalysis.personalDebt,
  );
  const debtAmount = convertToNumber(outstandingDebt);
  if (debtAmount != null) {
    return debtAmount + FINAL_EXPENSES;
  }
  return null;
};
const getPolicyStreamIncomeReplacementAmout = (
  form: QuoteWizardAnsweredForm,
): number | null => {
  const annualIncome = convertToNumber(
    form.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.needsAnalysis.annualIncome,
    ),
  );
  if (annualIncome === null) {
    return null;
  }

  const years = convertToNumber(
    form.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.needsAnalysis.yearsOfIncomeReplacement,
    ),
  );
  if (years === null) {
    return null;
  }
  return annualIncome * years;
};

const getPolicyStreamMortgageAmount = (
  form: QuoteWizardAnsweredForm,
): number | null => {
  const mortgage = convertToNumber(
    form.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.needsAnalysis.mortgageBalance,
    ),
  );
  return mortgage;
};
const getPolicyStreamEducationAmount = (
  form: QuoteWizardAnsweredForm,
): number | null => {
  const EDUCATION_AMOUNT_PER_CHILD = 25_000;
  const numberOfChildren = convertToNumber(
    form.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.needsAnalysis.numberOfChildren,
    ),
  );
  // this question is optional, so it might legitimately be blank -> not paying
  if (numberOfChildren === null || numberOfChildren <= 0) {
    return 0;
  }
  const payingForEducation =
    form.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.needsAnalysis.payingForChildrensEducation,
    ) === 'yes';
  if (payingForEducation) {
    return numberOfChildren * EDUCATION_AMOUNT_PER_CHILD;
  }

  return 0;
};

export const getRecommendedTermLength = (
  state: StoreState,
): StructuredPolicyStreamRatingData['policyLengthYears'] | null => {
  const age = getPolicyStreamClientAgeYears(state);
  if (age === null) {
    return null;
  }
  const rawTerm = 65 - age;
  // @ts-expect-error
  const term = findNearest(rawTerm, POLICY_TERM_YEARS);
  return term;
};

export const getPolicyStreamClientAgeYears = (
  state: StoreState,
): number | null => {
  try {
    const basicInfoForm = Object.values(
      state.policystreamStore.formAnswers,
    ).find(
      (answeredForm) =>
        answeredForm.form.internalName === POLICYSTREAM_FORMS.basicInfo,
    );

    if (!basicInfoForm) {
      return null;
    }

    if (basicInfoForm) {
      const dateOfBirthAnswer = basicInfoForm.getAnswerByApiName(
        POLICYSTREAM_API_NAMES.basicInfo.dateOfBirth,
      );
      if (!dateOfBirthAnswer) {
        return null;
      }
      const dateOfBirthString = dateOfBirthAnswer.value;
      const birthdate = parseDateMMDDYYYY(dateOfBirthString);
      if (!birthdate) {
        return null;
      }
      const age =
        (new Date().getTime() - birthdate.getTime()) / MILLISECONDS_PER_YEAR;
      return Math.floor(age);
    }
    return null;
  } catch (e) {
    console.error(`Error getting client age:`, e);
    Bugsnag.notify(errorify(e));
    return null;
  }
};

function parseDateMMDDYYYY(dateStr: string): Date | null {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;
  const match = dateStr.match(regex);

  if (!match) return null;

  const [, month, day, year] = match.map(Number);
  const date = new Date(year, month - 1, day);

  // Ensure the date components match the input to avoid invalid dates (e.g., "02/30/2024" becoming "03/01/2024")
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

const convertToNumber = (s: unknown): number | null => {
  if (typeof s === 'number') {
    return s;
  }
  if (s === null || s === undefined || s === '') {
    return null;
  }
  if (!(typeof s === 'string')) {
    return null;
  }
  try {
    const asNumber = Number(s);
    if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
      return asNumber;
    }
    return null;
  } catch (e) {
    console.error(`Error converting ${s} to number`, e);
    Bugsnag.notify(errorify(e));
    return null;
  }
};
